import React, { memo, useMemo } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { Container } from 'reactstrap';
import { FormHTMLInline } from '@core/components/form';
import { R } from '@core/utils/r';
import { TextBlockSettings } from './TextBlockSettings';
import { Paragraph } from './Paragraph';
import { useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { ProposalService } from '@business/services';

function defineTextWidgetForm() {
  return formGroup({
    heading: formControl<string>({ initialValue: 'Heading' }),
    paragraph: formControl<string>({
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
    paragraph2: formControl<string>({
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
    paragraph3: formControl<string>({
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
    paragraph4: formControl<string>({
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
    columnsWidths: formControl<string>({
      initialValue: '1',
      label: 'Columns Widths (ex: 1 2 1)',
    }),
    column1Background: formControl<string>({
      label: 'Column 1 Background',
    }),
    column2Background: formControl<string>({
      label: 'Column 2 Background',
    }),
    column3Background: formControl<string>({
      label: 'Column 3 Background',
    }),
    column4Background: formControl<string>({
      label: 'Column 4 Background',
    }),
    // Each column's own text colour; empty = the section's Text Color
    column1Color: formControl<string>({
      label: 'Column 1 Text Color',
    }),
    column2Color: formControl<string>({
      label: 'Column 2 Text Color',
    }),
    column3Color: formControl<string>({
      label: 'Column 3 Text Color',
    }),
    column4Color: formControl<string>({
      label: 'Column 4 Text Color',
    }),
    advance: defineBlockAdvanceForm(),
  });
}

export type TextBlockForm = ReturnType<typeof defineTextWidgetForm>;
export type TextBlockFormData = FormDataType<typeof defineTextWidgetForm>;

export enum TextBlockVariation {
  paragraph = 'paragraph',
  paragraphWithHeading = 'paragraphWithHeading',
  paragraphWithSubheading = 'paragraphWithSubheading',
  heading = 'heading',
  subheading = 'subheading',
  twoColumn = 'twoColumn',
  threeColumn = 'threeColumn',
  fourColumn = 'fourColumn',
}

export function columnCount(variation: TextBlockVariation): number {
  if (variation === TextBlockVariation.twoColumn) return 2;
  if (variation === TextBlockVariation.threeColumn) return 3;
  if (variation === TextBlockVariation.fourColumn) return 4;
  return 1;
}

export const TextBlock = memo((props: Proposal.BlockProps<TextBlockVariation>) => {
  const { variation, editable, isSection, sectionTitle } = props;
  const form = useMemo(defineTextWidgetForm, []);
  const formValue = useNonNilObservable(form.value$);
  const data = useProposalDataContext();

  useConfigureBlock({
    props,
    form,
    contentProps: { form, columns: columnCount(variation) },
    contentComponent: TextBlockSettings,
    onlyAdvance: true,
  });

  const hasHeading = useMemo(() => [TextBlockVariation.heading, TextBlockVariation.paragraphWithHeading].includes(variation), [variation]);
  const hasSubheading = useMemo(() => [TextBlockVariation.subheading, TextBlockVariation.paragraphWithSubheading].includes(variation), [variation]);
  const hasParagraph = useMemo(
    () =>
      [
        TextBlockVariation.paragraph,
        TextBlockVariation.paragraphWithHeading,
        TextBlockVariation.paragraphWithSubheading,
        TextBlockVariation.twoColumn,
        TextBlockVariation.threeColumn,
        TextBlockVariation.fourColumn,
      ].includes(variation),
    [variation],
  );

  const heading = useMemo(() => {
    if (!hasHeading && !hasSubheading) return null;
    const content = (
      <FormHTMLInline control={form.controls.heading} readonly={!editable} transformedFieldsValue={ProposalService.getTransformedValues(data)} />
    );
    // The theme colours headings grey; with a section Text Color set, the heading follows it like the rest of the text
    const style = formValue.advance.color ? { color: 'inherit' } : undefined;
    return hasHeading ? <h1 style={style}>{content}</h1> : <h3 style={style}>{content}</h3>;
  }, [data, editable, form.controls.heading, hasHeading, hasSubheading, formValue.advance.color]);

  const columns = useMemo(() => columnCount(variation), [variation]);

  const columnsWidths = useMemo(() => {
    const widths = !R.isEmpty(formValue.columnsWidths) ? formValue.columnsWidths.split(' ') : [];
    return R.times(columns).map(i => widths[i] || 1);
  }, [columns, formValue.columnsWidths]);

  return (
    <BlockContainer
      className="block-container d-flex flex-fill block-text"
      advanceForm={formValue.advance}
      excludeStyles={['borderRadius', 'borderWidth', 'borderColor', 'borderStyle', 'paddingTop', 'paddingBottom', 'paddingRight', 'paddingLeft']}
      style={{ padding: props.preview ? '30px 20px' : '0px' }}
      editable={editable}
      isSection={isSection}
      sectionTitle={sectionTitle}
      fullWidth={false}
      containerStyles={R.objectCompact({
        ...(formValue.advance.borderRadius ? { borderRadius: `${formValue.advance.borderRadius}px` } : {}),
        ...(formValue.advance.borderWidth ? { borderWidth: `${formValue.advance.borderWidth}px` } : {}),
        ...(formValue.advance.borderStyle ? { borderStyle: formValue.advance.borderStyle } : {}),
        ...(formValue.advance.borderColor ? { borderColor: formValue.advance.borderColor } : {}),
        ...(formValue.advance.paddingTop !== undefined ? { paddingTop: `${formValue.advance.paddingTop}px` } : {}),
        ...(formValue.advance.paddingBottom !== undefined ? { paddingBottom: `${formValue.advance.paddingBottom}px` } : {}),
        ...(formValue.advance.paddingLeft !== undefined ? { paddingLeft: `${formValue.advance.paddingLeft}px` } : {}),
        ...(formValue.advance.paddingRight !== undefined ? { paddingRight: `${formValue.advance.paddingRight}px` } : {}),
      })}
    >
      <div style={{ padding: '0 15px' }}>{heading}</div>
      {hasParagraph && (
        <div
          style={{
            fontSize: props.preview ? 14 : 'inherit',
            display: 'grid',
            gridTemplateColumns: R.times(columns)
              .map(i => `${columnsWidths[i]}fr`)
              .join(' '),
            columnGap: '1rem',
            rowGap: '2rem',
          }}
          className={`cols-${columns}`}
        >
          <Paragraph
            style={{ backgroundColor: formValue.column1Background, color: formValue.column1Color || undefined, padding: '30px 15px' }}
            control={form.controls.paragraph}
            editable={editable}
          />
          {columns > 1 && (
            <Paragraph
              style={{ backgroundColor: formValue.column2Background, color: formValue.column2Color || undefined, padding: '30px 15px' }}
              control={form.controls.paragraph2}
              editable={editable}
            />
          )}
          {columns > 2 && (
            <Paragraph
              style={{ backgroundColor: formValue.column3Background, color: formValue.column3Color || undefined, padding: '30px 15px' }}
              control={form.controls.paragraph3}
              editable={editable}
            />
          )}
          {columns > 3 && (
            <Paragraph
              style={{ backgroundColor: formValue.column4Background, color: formValue.column4Color || undefined, padding: '30px 15px' }}
              control={form.controls.paragraph4}
              editable={editable}
            />
          )}
        </div>
      )}
    </BlockContainer>
  );
});
